import AttendanceHistory from "./AttendanceHistory";
import TaskView from "./TaskView";
import { useCallback, useState } from "react";
import GoalsView from "./GoalsView";
import LeaveRequestHistory from "./LeaveRequestHistory";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { isTabId, TabId, TabNav } from "src/components/ui/TabNav";
import { ArrowLeft } from "lucide-react";

const AttendanceDetails = () => {
  const { empId } = useParams();
  const employeeId = Number(empId);

  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  
  const employeeName = searchParams.get("name");
  
  const tabParam = searchParams.get("tab");
  const activeTab: TabId = isTabId(tabParam) ? tabParam : "attendance";

  const changeTab = useCallback(
    (id: TabId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedAttendanceId, setSelectedAttendanceId] = useState<
    string | null
  >(null);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setSelectedAttendanceId(null);
  };

  if (!Number.isFinite(employeeId)) {
    return (
      <div className="p-6 text-sm text-slate-600">
        This employee link isn't valid. Go back to the employee list and try
        again.
      </div>
    );
  }

  return (
    <div className="pt-6">
      <div className="sticky top-0 backdrop-blur-lg z-10">
        <div className="flex items-center gap-2 mb-2 ">
          <button
            className="cursor-pointer p-2 rounded-full hover:bg-gray-200 transition-colors ease-in duration-300"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={20} />
          </button>
          <p className="capitalize text-md text-primary font-semibold">
            Details of {employeeName}
          </p>
        </div>

        <TabNav active={activeTab} onChange={changeTab} />
      </div>

      <div
        role="tabpanel"
        id={`panel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
        tabIndex={0}
        className="outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
      >
        {activeTab === "attendance" && (
          <div className="grid gap-6 grid-cols-12 items-start mt-6">
            <section
              aria-label="Attendance history"
              className="min-w-0 col-span-3 sticky top-24"
            >
              <AttendanceHistory
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
                onAttendanceSelect={setSelectedAttendanceId}
              />
            </section>

            <section
              aria-label="Tasks for selected day"
              className="min-w-0 col-span-9"
            >
              <TaskView
                selectedDate={selectedDate}
                attendanceId={selectedAttendanceId}
              />
            </section>
          </div>
        )}

        {activeTab === "leave" && (
          <LeaveRequestHistory employeeId={employeeId} />
        )}

        {activeTab === "goals" && <GoalsView />}
      </div>
    </div>
  );
};

export default AttendanceDetails;
